import CardValidator from 'card-validator'

import API from '@/api/base'
import { validationError, validateField } from '@/utils/form-helpers'
import { getCartId } from '@utils/shopping-cart'
import moment from 'moment'

export const state = {
  // Customer object
  customer: {},

  // Primary customer fields
  primary: {
    firstName: '',
    lastName: '',
    studentId: '',
    password: '',
    confirmPassword: '',
    emailAddress: '',
    confirmEmailAddress: '',
    phoneNumber: '',
    mobileNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  },

  // Secondary customer fields
  secondary: {
    firstName: '',
    lastName: '',
    emailAddress: '',
    confirmEmailAddress: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  },

  // Payment fields
  paymentInfo: {
    ccFirstName: '',
    ccLastName: '',
    ccCardNumber: '',
    ccCardType: '',
    ccCardExpiration: '',
    ccSecurityCode: '',
  },

  // Delivery fields
  delivery: {
    startDate: '',
    endDate: '',
    additionalNotes: '',
    building: '',
    room: '',
    roomLabel: '',
    roomNo: '',
  },

  // Shipping fields
  shipping: {
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  },
}

export const getters = {
  getCustomer: ({ customer }) => customer,
  getPrimaryCustomerFormField: (state) => (field) => state.primary[field],
  getSecondaryCustomerFormField: (state) => (field) => state.secondary[field],
  getShippingFormField: (state) => (field) => state.shipping[field],
  getDeliveryFormField: (state) => (field) => state.delivery[field],
  getPaymentFormField: (state) => (field) => state.paymentInfo[field],
  getDelivery: (state) => state.delivery,
  getPayment: (state) => {
    // clone state information without the bindings
    const payment = JSON.parse(JSON.stringify(state.paymentInfo))
    const validator = CardValidator.expirationDate(payment.ccCardExpiration)
    payment.ccCardExpirationMonth = validator.month
    payment.ccCardExpirationYear = validator.year
    return payment
  },
  getCustomerFormSubmissionObj: (state) => {
    return {
      firstName: state.primary.firstName,
      lastName: state.primary.lastName,
      email: state.primary.emailAddress,
      studentId: state.primary.studentId,
      mobile: state.primary.mobileNumber,
      phone: state.primary.phoneNumber,
      address: state.primary.address,
      city: state.primary.city,
      state: state.primary.state,
      zip: state.primary.zipCode,
    }
  },
  getShipping: (state) => state.shipping,
}

export const actions = {
  createCustomer: ({ commit, state, getters, rootGetters, dispatch }) =>
    new Promise((resolve, reject) => {
      const customer = getters.getCustomerFormSubmissionObj
      customer.shoppingCarts = [
        {
          id: getCartId(),
        },
      ]
      // not needed for creation
      delete customer.mobile
      // Append password if present (if present, it has been validated)
      if (state.primary.password.length > 0) {
        customer.password = state.primary.password
      }

      // Temporary solution until public site authenticated customer tokens are worked out
      const loggedIn = rootGetters['auth/loggedIn']
      customer.meta = loggedIn

      API.create(
        'customer',
        customer,
        {},
        {
          logged_in: loggedIn,
        }
      )
        .then((response) => {
          commit('CREATE_CUSTOMER', response.data)
          // If logged in, persists changes to auth module as well
          if (loggedIn) {
            dispatch('auth/saveUser', response.data, {
              root: true,
            })
          }
          resolve(response.data)
        })
        .catch((error) => {
          const msg =
            'An error occurred while attempting to process your order.' +
            ' Please check your information and try again.'
          reject(validationError('customer', msg, error))
        })
    }),
  setCustomerForm({ commit }, customer) {
    Object.keys(customer).forEach((key) => {
      let niceKey = key
      if (key === 'zip') {
        // Handle zip to zipCode
        niceKey = 'zipCode'
      } else if (key === 'email') {
        // Handle emailAddress
        niceKey = 'emailAddress'
        // Handle confirmEmailAddress
        commit({
          type: 'SET_PRIMARY_CUSTOMER_FORM',
          field: 'confirmEmailAddress',
          value: customer[key],
        })
      } else if (key === 'phone') {
        // Handle phoneNumber
        niceKey = 'phoneNumber'
      } else if (key === 'mobile') {
        niceKey = 'mobileNumber'
      }
      commit({
        type: 'SET_PRIMARY_CUSTOMER_FORM',
        field: niceKey,
        value: customer[key],
      })
    })
  },

  updatePassword: (placeholder, params) =>
    new Promise((resolve, reject) => {
      API.update('customer', {
        password: params.password,
        id: params.public_key,
      })
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    }),

  resetCustomer({ commit }) {
    commit('CREATE_CUSTOMER', {})
  },

  resetPrimaryForm({ commit, rootGetters, state }) {
    // Only reset primary form if user is not logged in
    if (!rootGetters['auth/loggedIn']) {
      Object.keys(state.primary).forEach((key) => {
        commit({
          type: 'SET_PRIMARY_CUSTOMER_FORM',
          field: key,
          value: '',
        })
      })
    }
  },

  resetSecondaryForm({ commit, state }) {
    Object.keys(state.secondary).forEach((key) => {
      commit({
        type: 'SET_SECONDARY_CUSTOMER_FORM',
        field: key,
        value: '',
      })
    })
  },

  resetDeliveryForm({ commit, state }) {
    Object.keys(state.delivery).forEach((key) => {
      commit({
        type: 'SET_DELIVERY_FORM',
        field: key,
        value: '',
      })
    })
  },

  resetPaymentForm({ commit, state }) {
    Object.keys(state.paymentInfo).forEach((key) => {
      commit({
        type: 'SET_PAYMENT_FORM',
        field: key,
        value: '',
      })
    })
  },

  updatePrimaryField({ commit }, payload) {
    commit({
      type: 'SET_PRIMARY_CUSTOMER_FORM',
      field: payload.field,
      value: payload.value,
    })
  },

  updateSecondaryField({ commit }, payload) {
    commit({
      type: 'SET_SECONDARY_CUSTOMER_FORM',
      field: payload.field,
      value: payload.value,
    })
  },

  updateDeliveryField({ commit }, payload) {
    commit({
      type: 'SET_DELIVERY_FORM',
      field: payload.field,
      value: payload.value,
    })
  },

  updatePaymentField({ commit }, payload) {
    commit({
      type: 'SET_PAYMENT_FORM',
      field: payload.field,
      value: payload.value,
    })
  },

  updateShippingField({ commit }, payload) {
    commit({
      type: 'SET_SHIPPING_FORM',
      field: payload.field,
      value: payload.value,
    })
  },

  // passwordHandling is a string: 'required', 'optional', 'omit'
  // mobile is a boolean; true means validate mobile field, otherwise ignore it
  // confirmEmail is a boolean, same as mobile
  evaluateCustomerValidity: (
    { state },
    { passwordHandling, mobile, confirmEmail, paymentMethod }
  ) =>
    new Promise((resolve, reject) => {
      const genericRegex = /^[a-zA-Z0-9 ]+$/

      // initialize object that tracks validity for each field of primary user

      const primary = {}
      Object.keys(state.primary).forEach((key) => {
        // all fields are initially false (invalid)
        primary[key] = false
      })

      // Delete id field in case it is present
      delete primary.id
      delete primary.type

      primary.firstName = validateField(state.primary.firstName, [
        'required',
        'formName',
      ])
      primary.lastName = validateField(state.primary.lastName, [
        'required',
        'formName',
      ])
      primary.emailAddress = validateField(state.primary.emailAddress, [
        'required',
        'email',
      ])
      if (confirmEmail) {
        primary.confirmEmailAddress = validateField(
          state.primary.confirmEmailAddress,
          ['is'],
          state.primary.emailAddress
        )
      } else {
        delete primary.confirmEmailAddress
      }
      primary.phoneNumber = validateField(state.primary.phoneNumber, [
        'required',
        'phone',
      ])

      // mobileNumber field is always optional, only validate if value exists
      if (mobile) {
        primary.mobileNumber = validateField(state.primary.mobileNumber, [
          'optionalPhone',
        ])
      } else {
        delete primary.mobileNumber
      }

      primary.address = validateField(state.primary.address, ['required'])
      primary.address2 = true
      primary.city = validateField(state.primary.city, ['required'])
      primary.state = validateField(state.primary.state, ['required', 'state'])
      primary.zipCode = validateField(state.primary.zipCode, [
        'required',
        'numeric',
      ])
      if (paymentMethod === 'student_id') {
        // primary.studentId = validateField(state.primary.studentId, [
        //   ['required', 'regex'],
        //   genericRegex,
        // ])
        const primaryStudentIdRegex = validateField(
          state.primary.studentId,
          ['regex'],
          genericRegex
        )
        const studentIDSiteDefinedPattern = true
        // Support for site-defined student id pattern, here in case we need it in the future
        // if (rootGetters.getStudentIdPattern.length > 0) {
        //   const studentIdRegex = new RegExp(`^${rootGetters.getStudentIdPattern}$`)
        //   studentIDSiteDefinedPattern = studentIdRegex.test(state.primary.studentId)
        // }
        primary.studentId = primaryStudentIdRegex && studentIDSiteDefinedPattern
      } else {
        primary.studentId = true
      }

      // If password is required or optional
      if (passwordHandling === 'optional' || passwordHandling === 'required') {
        // require password if passwordHandling is required
        if (passwordHandling === 'required') {
          primary.password = validateField(state.primary.password, ['required'])
        }
        primary.password = validateField(
          state.primary.password,
          ['minIfOne'],
          8
        )
        primary.confirmPassword = validateField(
          state.primary.confirmPassword,
          ['is'],
          state.primary.password
        )
      } else {
        delete primary.password
        delete primary.confirmPassword
      }

      // Returns an array of fields that are invalid
      const invalid = Object.entries(primary)
        .filter(([_, value]) => {
          return value === false
        })
        .map(([key, _]) => key)

      // If primary doesn't have any errors, set validity to true!
      if (invalid.length === 0) {
        resolve()
      } else {
        // We can customize the message or add an object as an
        // additional param to the `payload` field of the error
        reject(
          validationError('customerForm', 'Please enter all required fields.')
        )
      }
    }),

  evaluateSecondaryCustomerValidity: ({ state }) =>
    new Promise((resolve, reject) => {
      const genericRegex = /^[a-zA-Z0-9 ]+$/
      // initialize object that tracks validity for each field of secondary user
      const secondary = {}
      Object.keys(state.secondary).forEach((key) => {
        // all fields are initially false (invalid)
        secondary[key] = false
      })
      secondary.firstName = validateField(state.secondary.firstName, [
        'required',
        'alpha',
      ])
      secondary.lastName = validateField(state.secondary.lastName, [
        'required',
        'alpha',
      ])
      secondary.emailAddress = validateField(
        state.secondary.emailAddress,
        ['required', 'email', 'isNot'],
        state.primary.emailAddress
      )
      secondary.confirmEmailAddress = validateField(
        state.secondary.confirmEmailAddress,
        ['is'],
        state.secondary.emailAddress
      )
      secondary.phoneNumber = validateField(state.secondary.phoneNumber, [
        'required',
        'phone',
      ])
      secondary.address = validateField(
        state.secondary.address,
        ['required', 'regex'],
        genericRegex
      )
      secondary.city = validateField(
        state.secondary.city,
        ['required', 'regex'],
        genericRegex
      )
      secondary.state = validateField(state.secondary.state, [
        'required',
        'state',
      ])
      secondary.zipCode = validateField(state.secondary.zipCode, [
        'required',
        'numeric',
      ])

      // Returns an array of fields that are invalid
      const invalid = Object.entries(secondary)
        .filter(([_, value]) => {
          return value === false
        })
        .map(([key, _]) => key)

      // If secondary doesn't have any errors, set validity to true!
      if (invalid.length === 0) {
        resolve()
      } else {
        // We can customize the message or add an object as an
        // additional param to the `payload` field of the error
        reject(
          validationError('studentForm', 'Please enter all required fields.')
        )
      }
    }),

  evaluateDeliveryOptions: ({ state }) =>
    new Promise((resolve, reject) => {
      // initialize object that tracks validity for each field of delivery
      const delivery = {}
      Object.keys(state.delivery).forEach((key) => {
        // all fields are initially false (invalid)
        delivery[key] = false
      })

      // Validate start and end dates
      delivery.startDate = validateField(state.delivery.startDate, [
        'required',
        'isDate',
      ])
      delivery.endDate = validateField(state.delivery.endDate, [
        'required',
        'isDate',
      ])

      // convert start/end dates to momentjs values
      const start = moment(state.delivery.startDate)
      const end = moment(state.delivery.endDate)
      // use momentjs comparison to test if start and end dates are at least 4 weeks apart
      delivery.endDate = delivery.endDate && start.diff(end, 'w') < 4

      // Delivery options
      delivery.building = validateField(state.delivery.building, [
        'required',
        'formName',
      ])
      delivery.room = validateField(state.delivery.room, [
        'required',
        'alphaNumeric',
      ])
      delivery.additionalNotes = true

      // Returns an array of fields that are invalid
      const invalid = Object.entries(delivery)
        .filter(([_, value]) => {
          return value === false
        })
        .map(([key, _]) => key)

      // If delivery doesn't have any errors, set validity to true!
      if (invalid.length === 0) {
        resolve()
      } else {
        // We can customize the message or add an object as an
        // additional param to the `payload` field of the error
        reject(
          validationError('deliveryForm', 'Please enter all required fields.')
        )
      }
    }),

  evaluateRoomNo: ({ state }, { fulfillmentMethod, useRoomNo }) =>
    new Promise((resolve, reject) => {
      const delivery = {}
      delivery.roomNo = validateField(state.delivery.roomNo, ['required'])
      // Returns an array of fields that are invalid
      const invalid = Object.entries(delivery)
        .filter(([_, value]) => {
          return value === false
        })
        .map(([key, _]) => key)

      // If delivery doesn't have any errors, set validity to true!
      if (
        invalid.length === 0 ||
        fulfillmentMethod !== 'delivery' ||
        useRoomNo === false
      ) {
        resolve()
      } else {
        // We can customize the message or add an object as an
        // additional param to the `payload` field of the error
        reject(
          validationError('deliveryForm', 'Please enter all required fields.')
        )
      }
    }),

  evaluateShippingValidity: ({ state }, { fulfillmentMethod }) =>
    new Promise((resolve, reject) => {
      // initialize object that tracks validity for each field of primary user

      const shipping = {}
      Object.keys(state.shipping).forEach((key) => {
        // all fields are initially false (invalid)
        shipping[key] = false
      })

      // Delete id field in case it is present
      delete shipping.id
      delete shipping.type

      shipping.firstName = validateField(state.shipping.firstName, [
        'required',
        'formName',
      ])
      shipping.lastName = validateField(state.shipping.lastName, [
        'required',
        'formName',
      ])
      shipping.emailAddress = true
      shipping.phoneNumber = validateField(state.shipping.phoneNumber, [
        'required',
        'phone',
      ])

      shipping.address = validateField(state.shipping.address, ['required'])
      shipping.address2 = true
      shipping.city = validateField(state.shipping.city, ['required'])
      shipping.state = validateField(state.shipping.state, [
        'required',
        'state',
      ])
      shipping.zipCode = validateField(state.shipping.zipCode, [
        'required',
        'numeric',
      ])

      // Returns an array of fields that are invalid
      const invalid = Object.entries(shipping)
        .filter(([_, value]) => {
          return value === false
        })
        .map(([key, _]) => key)

      // If shipping doesn't have any errors, set validity to true!
      if (invalid.length === 0 || fulfillmentMethod !== 'ship') {
        resolve()
      } else {
        // We can customize the message or add an object as an
        // additional param to the `payload` field of the error
        reject(
          validationError(
            'customerForm',
            'A field in the shipping form is invalid.'
          )
        )
      }
    }),

  evaluateCreditCardValidity: ({ state }, { paymentMethod }) =>
    new Promise((resolve, reject) => {
      // initialize object that tracks validity for each field of paymentInfo
      const payment = {}
      Object.keys(state.paymentInfo).forEach((key) => {
        // all fields are initially false (invalid)
        payment[key] = false
      })

      // Credit card fields
      payment.ccFirstName = validateField(state.paymentInfo.ccFirstName, [
        'required',
        'alpha',
      ])
      payment.ccLastName = validateField(state.paymentInfo.ccLastName, [
        'required',
        'alpha',
      ])
      payment.ccCardExpiration = validateField(
        state.paymentInfo.ccCardExpiration,
        ['required', 'creditCardExpiry']
      )
      payment.ccSecurityCode = validateField(
        state.paymentInfo.ccCardExpiration,
        ['required', 'creditCardCVV'],
        state.paymentInfo.ccCardNumber
      )

      // Validate credit card number, type, and cvv
      const validation = CardValidator.number(state.paymentInfo.ccCardNumber)
      if (validation && validation.card) {
        payment.ccCardNumber = validation.isValid
        // Card type
        const cType = validation.card.niceType
        if (
          cType === 'American Express' ||
          cType === 'Mastercard' ||
          cType === 'Visa'
        ) {
          const cardTypeField = state.paymentInfo.ccCardType
          if (cType === 'Mastercard' && cardTypeField === 'MasterCard') {
            payment.ccCardType = true
          } else {
            payment.ccCardType = true
          }
        }
      }

      // Returns an array of fields that are invalid
      const invalid = Object.entries(payment)
        .filter(([_, value]) => {
          return value === false
        })
        .map(([key, _]) => key)

      // If payment doesn't have any errors, set validity to true!
      if (invalid.length === 0 || paymentMethod === 'student_id') {
        resolve()
      } else {
        // We can customize the message or add an object as an
        // additional param to the `payload` field of the error
        reject(
          validationError('creditCardForm', 'Please enter all required fields.')
        )
      }
    }),
  logFormsToConsole: ({ state }) => {
    // console.log('Primary customer form:')
    // Object.keys(state.primary).forEach((key) => {
    //   console.log(`${key}: ${state.primary[key]}`)
    // })
    // console.log('\nSecondary customer form:')
    // Object.keys(state.secondary).forEach((key) => {
    //   console.log(`${key}: ${state.secondary[key]}`)
    // })
    // console.log('\nPayment info form:')
    // Object.keys(state.paymentInfo).forEach((key) => {
    //   console.log(`${key}: ${state.paymentInfo[key]}`)
    // })
    // console.log('\nDelivery form:')
    // Object.keys(state.delivery).forEach((key) => {
    //   console.log(`${key}: ${state.delivery[key]}`)
    // })
  },
}

export const mutations = {
  CREATE_CUSTOMER(state, customer) {
    state.customer = customer
  },
  SET_PRIMARY_CUSTOMER_FORM(state, payload) {
    state.primary[payload.field] = payload.value
  },
  SET_SECONDARY_CUSTOMER_FORM(state, payload) {
    state.secondary[payload.field] = payload.value
  },
  SET_DELIVERY_FORM(state, payload) {
    state.delivery[payload.field] = payload.value
  },
  SET_PAYMENT_FORM(state, payload) {
    state.paymentInfo[payload.field] = payload.value
  },
  SET_SHIPPING_FORM(state, payload) {
    state.shipping[payload.field] = payload.value
  },
}
